'use client';

import { c as _c } from "react/compiler-runtime";
import { Auth0Provider } from '@/components/layout/auth0.provider';
import { ReactQueryProvider } from '@/providers/react-query/react-query.provider';
import { DialogProvider } from '@/contexts/dialogs';
import React, { PropsWithChildren, useState, type JSX } from 'react';
import { AuthenticatedLayout } from '@/components/layout/authenticated.layout';
import { AuthProvider } from '@/hooks/use-auth';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { useSearchParams } from 'next/navigation';
import { UpdateProvider } from '@/providers/update-provider';
import { Auth0LogoutProvider, TenantLogoutProvider } from '@/providers/logout.provider';
import { Auth0AuthenticatedLayout } from './auth0-authenticated.layout';
import { Toaster } from '@/comps/primitives/sonner';
import { FilterUsageCleanupListener } from '@/providers/filter-usage-cleanup';
import { ProjectsProvider } from '@/providers/projects-provider';
import { GlobalFiltersProvider } from '@/contexts/filters/filter-context';
import { AliasProvider } from '@/contexts/alias';
import { TaxonomySettingsProvider } from '@/contexts/taxonomy-settings';
import { ContactChannel } from '@/providers/contact-channel.provider';
function get_auth0_layout({
  children
}: PropsWithChildren): JSX.Element {
  // Return the layout for Auth0. To be removed as soon as we leave Auth0.
  return <Auth0Provider>
      <Auth0LogoutProvider>
        <AuthProvider isAuth0={true}>
          <Auth0AuthenticatedLayout>
            <ReactQueryProvider>
              <ContactChannel.Base>
                <ProjectsProvider>
                  <TaxonomySettingsProvider>
                    <AliasProvider>
                      <GlobalFiltersProvider>
                        <DialogProvider>
                          <UpdateProvider>
                            {/*TODO: fix this type casting*/}
                            {children}
                          </UpdateProvider>
                        </DialogProvider>
                      </GlobalFiltersProvider>
                    </AliasProvider>
                  </TaxonomySettingsProvider>
                </ProjectsProvider>
              </ContactChannel.Base>
              <FilterUsageCleanupListener />
            </ReactQueryProvider>
          </Auth0AuthenticatedLayout>
        </AuthProvider>
      </Auth0LogoutProvider>
    </Auth0Provider>;
}
function get_auth_layout({
  children
}: PropsWithChildren): JSX.Element {
  return <TenantLogoutProvider>
      <AuthProvider isAuth0={false}>
        <AuthenticatedLayout>
          <ReactQueryProvider>
            <ContactChannel.Base>
              <ProjectsProvider>
                <TaxonomySettingsProvider>
                  <AliasProvider>
                    <GlobalFiltersProvider>
                      <DialogProvider>
                        <UpdateProvider>
                          {/*TODO: fix this type casting*/}
                          {children}
                        </UpdateProvider>
                      </DialogProvider>
                    </GlobalFiltersProvider>
                  </AliasProvider>
                </TaxonomySettingsProvider>
              </ProjectsProvider>
            </ContactChannel.Base>
            <FilterUsageCleanupListener />
          </ReactQueryProvider>
        </AuthenticatedLayout>
      </AuthProvider>
    </TenantLogoutProvider>;
}
export const AuthenticatedAppLayout = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    authMode
  } = useEnvironmentContext();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("token");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [isSharedLink] = useState(!!t1);
  if (isSharedLink) {
    let t2;
    if ($[2] !== children) {
      t2 = get_auth_layout({
        children
      });
      $[2] = children;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== authMode || $[5] !== children) {
    t2 = authMode === "auth0" ? get_auth0_layout({
      children
    }) : get_auth_layout({
      children
    });
    $[4] = authMode;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const PublicAppLayout = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Toaster />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <DialogProvider><UpdateProvider>{t1}{children}</UpdateProvider></DialogProvider>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <FilterUsageCleanupListener />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <TenantLogoutProvider><ReactQueryProvider>{t2}{t3}</ReactQueryProvider></TenantLogoutProvider>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};